// Import the Moment.js library for handling dates and times
import moment from "moment";
import axios from 'axios';
import { toast } from "react-toastify";
import { msalInstance } from "./msalInstance";
import { MSAL_LOGINREQUEST } from "../config";
import { getData, PROVIDER, TOKEN } from "./Storage";
import { decodeJwt } from "jose";
import { InteractionRequiredAuthError, BrowserAuthError } from "@azure/msal-browser";

/**
 * Asynchronously checks if the network is connected.
 * 
 * @returns {Promise<boolean>} Returns a boolean value indicating network status.
 */
export const _isNetworkConnected = async () => {
  // Currently hardcoded to return false, this function could be
  // implemented to actually check network status
  return false;
};

/**
 * Formats a date to DD/MM/YYYY format.
 * 
 * @param {any} date - The date to be formatted.
 * @returns {string} Returns the date formatted as a string in DD/MM/YYYY.
 */
export const dateFormat = (date: any) => {
  // Use Moment.js to format the date to DD/MM/YYYY
  const finalDate = moment(date).format('DD/MM/YYYY');
  return finalDate;
};

/**
 * Encodes an object to a URL-encoded string format.
 * 
 * @param {any} data - The object to be encoded.
 * @returns {string} Returns a URL-encoded string.
 */
export const encodeFormData = (data: any) => {
  // Initialize an array to hold each encoded item
  let formBody = [];

  // Loop through each property in the object
  for (let property in data) {
    // Encode the property name
    let encodedKey = encodeURIComponent(property);
    // Encode the property value
    let encodedValue = encodeURIComponent(data[property]);
    // Add them to the array in key=value format
    formBody.push(encodedKey + '=' + encodedValue);
  }
  // Join the array into a single string, separating items with '&'
  let formBodystr = formBody.join('&');

  return formBodystr;
};


export const toSeoUrl = (url: string) => {

  // make the url lowercase         
  let encodedUrl: string = url.toString().toLowerCase();

  // replace & with and           
  encodedUrl = encodedUrl.split(/\&+/).join("-and-")

  // remove invalid characters 
  encodedUrl = encodedUrl.split(/[^a-z0-9]/).join("-");

  // remove duplicates 
  encodedUrl = encodedUrl.split(/-+/).join("-");

  // trim leading & trailing characters 
  encodedUrl = encodedUrl.trim();

  return encodedUrl;
}

interface _TokenProps {
  isLegacy: boolean,
  token: string
}


const __handleError = (error: any) => {
  if (error.response) {

    switch (error.response.status) {
      case 401:
        toast.error(error.response.data);
        // Redirect to login page
        setTimeout(() => {
          toast.error("user logged out. common utils 90");
          localStorage.clear();
          window.location.reload();
        }, 3000);
        break;

      case 403:
        // Redirect to forbidden page

        break;
      case 500:
        // Redirect to forbidden page
        // toast.error("Internal server error");
        break;

      default:
        break;
    }

  }
  return Promise.reject(error);
}


export const isTokenExpired = (token: string): boolean => {
  try {
    const { exp } = decodeJwt(token);
    if (!exp) return true;
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
}

export const getAxiosInstanceWithoutToken = () => {
  const axiosInstance = axios.create({ headers: {} });
  axiosInstance.interceptors.response.use(
    response => response,
    error => __handleError(error)
  )

  // Create axios instance with headers
  return axiosInstance

}

export const getHeaders = (token: _TokenProps) => {
  let headers = {};
  if (token !== null && token !== undefined && token.token) {
    if (token?.isLegacy) {
      headers = {
        "X-Core-Key": token.token,
      }
    } else {
      headers = {
        "Authorization": `Bearer ${token.token}`
      }
    }
  }

  return headers;
}


export const getNewAxiosInstance = (params: any = { headers: {}, reseponseType: 'json' }) => {
  // Check if token is null, undefined, or missing token property
  // if (!token || token === null || token === undefined || !token.token) {
  //   console.error("Token is invalid or missing. Redirecting to login.");
  //   handleExpiredToken("Authentication required. Please log in.");
  //   // Return a basic axios instance that will be used before redirect completes
  //   return axios.create({ ...params });
  // }

  // if (token && token.token) {
  //   if (token?.isLegacy) {
  //     params.headers = {
  //       "X-Core-Key": token.token,
  //     }
  //   } else {
  //     params.headers = {
  //       "Authorization": `Bearer ${token.token}`
  //     }
  //   }
  // } else {
  //   //redirect to login page if token is invalid or missing
  //   console.error("Token is invalid or missing. Redirecting to login.");
  //   handleExpiredToken("Authentication required. Please log in.");
  // }
  let isRedirectingToLogin = false;

  const handleSessionExpiry = (message = "Session expired. Redirecting to login.") => {
    if (isRedirectingToLogin) return;
    isRedirectingToLogin = true;
    toast.error(message);
    setTimeout(() => {
      localStorage.clear();
      window.location.reload();
    }, 3000);
  };

  const axiosInstance = axios.create({ ...params });

  axiosInstance.interceptors.request.use(
    async (config) => {
      try {
        const abortController: any = new AbortController();

        const provider = await getData(PROVIDER);


        if (provider === "legacy") {
          const token: any = await getData(TOKEN);
          const isInvalid = !token || !token.token || isTokenExpired(token.token);

          if (isInvalid) {
            handleSessionExpiry();
            // Abort the request properly
            abortController.abort("Token is invalid or expired.");
            config.signal = abortController.signal;
            return Promise.reject(new Error("Token is invalid or expired."));
          }


          config.headers["X-Core-Key"] = token?.token;

        } else {

          const activeAccount = msalInstance.getActiveAccount();
          // Case: No active MSAL account at all
          if (!activeAccount) {
            handleSessionExpiry("No active session found. Redirecting to login.");
            return Promise.reject(new Error("No active MSAL account."));
          }


          try {
            const res = await msalInstance.acquireTokenSilent({
              ...MSAL_LOGINREQUEST,
              account: activeAccount,
            });

            config.headers.Authorization = `Bearer ${res.accessToken}`;

          } catch (msalError) {
            if (msalError instanceof InteractionRequiredAuthError) {
              // Refresh token expired — user must re-authenticate
              // Option A: Redirect to Microsoft login
              await msalInstance.acquireTokenRedirect({
                ...MSAL_LOGINREQUEST,
                account: activeAccount,
              });
              // Option B: If you prefer popup instead
              // const res = await msalInstance.acquireTokenPopup({ ...MSAL_LOGINREQUEST });
              // config.headers.Authorization = `Bearer ${res.accessToken}`;
            } else if (msalError instanceof BrowserAuthError) {
              // Browser-level issues (e.g., popup blocked, interaction in progress)
              console.error("BrowserAuthError:", msalError);
              handleSessionExpiry("Authentication error. Please log in again.");
              return Promise.reject(msalError);
            } else {
              // Unknown MSAL error
              console.error("MSAL unexpected error:", msalError);
              handleSessionExpiry();
              return Promise.reject(msalError);
            }
          }
        }

        return config;
      } catch (error) {
        if (!isRedirectingToLogin) {
          console.error("Interceptor error:", error);
          handleSessionExpiry();
        }
        return Promise.reject(error);
      }
    },
    error => Promise.reject(error)

  )


  axiosInstance.interceptors.response.use(
    response => response,
    error => __handleError(error)
  )

  // Create axios instance with headers
  return axiosInstance
}